import { Button } from '@/app/common/components/button/Button'
import { Card, CardContent, CardHeader } from '@/common/components/Card'
import FormErrorMessage from '@/common/components/FormErrorMessage'
import { Input } from '@/app/common/components/input/Input'
import { EyeIcon, EyeSlashIcon } from '@/common/icons/general'
import { useAppAlert } from '@/layouts/AppAlertProvider'
import { removeNonDigitsFromNumber } from '@/lib/utils'
import { UpdateUserData } from '@/queries/users/requests'
import { RootState } from '@/storage/store'
import { setUserData } from '@/storage/user/authSlice'
import { zodResolver } from '@hookform/resolvers/zod'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { z } from 'zod'

interface Props {
  id: number
  phone: string
}

const formSchema = z.object({
  twoFa: z
    .string()
    .refine((val) => !val || /^\(\d{3}\) \d{3}-\d{4}$/.test(val), {
      message: 'Invalid phone number format',
    }),
  current_password: z.string(),
})

type UpdatePayloadReq = {
  phone: string
  context: 'log_in_method'
  password?: string
}

type FormSchema = z.infer<typeof formSchema>

function TwoFaSettings(props: Props) {
  const { id, phone } = props
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const { fireAlert } = useAppAlert()

  const dispatch = useDispatch()
  const user = useSelector((state: RootState) => state.auth.user)

  const onSubmit = async (formData: FormSchema) => {
    const formattedNumber = removeNonDigitsFromNumber(formData.twoFa)
    if (formattedNumber === phone) {
      fireAlert({
        variant: 'error',
        message: 'Please enter a new phone number',
      })
      return
    }

    const payload: UpdatePayloadReq = {
      phone: formData.twoFa && formattedNumber,
      password: formData.current_password,
      context: 'log_in_method',
    }
    const { error } = await UpdateUserData(payload, id)

    if (error) {
      fireAlert({
        variant: 'error',
        message: error.message,
      })
    } else {
      dispatch(setUserData({ ...user, phone: payload.phone }))

      fireAlert({
        variant: 'success',
        message: '2FA updated successfully',
      })
    }
  }

  function formatPhoneNumber(value: string) {
    const formattedValue = value.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
    return formattedValue
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormSchema>({
    defaultValues: {
      current_password: '',
      twoFa: formatPhoneNumber(phone),
    },
    resolver: zodResolver(formSchema),
  })

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '')
    if (value.length > 10) value = value.slice(0, 10)

    const formattedValue = formatPhoneNumber(value)
    setValue('twoFa', formattedValue)
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="text-lg font-semibold">2Fa Settings</h2>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <label className="block font-medium mb-1">2Fa</label>
            <Input
              type="text"
              {...register('twoFa')}
              placeholder="Enter 2Fa"
              autoComplete="off"
              onChange={handlePhoneInputChange}
            />
            <FormErrorMessage message={errors.twoFa?.message} />
          </div>{' '}
          <div className="mb-4">
            <label
              htmlFor="current_password"
              className="block font-medium mb-1"
            >
              Current Password
            </label>
            <div className="relative flex">
              <Input
                type={showCurrentPassword ? 'text' : 'password'}
                {...register('current_password')}
                placeholder="Enter current password"
              />
              <div
                className="absolute right-0 flex items-center h-full mr-2"
                onClick={() => {
                  setShowCurrentPassword(!showCurrentPassword)
                }}
              >
                {showCurrentPassword ? <EyeIcon /> : <EyeSlashIcon />}
              </div>
            </div>
            <FormErrorMessage message={errors.current_password?.message} />
          </div>
          <div className="text-end">
            <Button type="submit">Save Changes</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default TwoFaSettings
